import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Link, Navigate, RouterProvider, useRouteError } from "react-router-dom";
import { Toaster } from "sonner";
import { AppAuthProvider, ProtectedRoute, SignInPage, SignUpPage } from "./auth";
import { queryClient } from "./lib/queryClient";
import "./styles.css";

const Dashboard = React.lazy(async () => ({ default: (await import("./pages/Dashboard")).Dashboard }));
const SessionSetup = React.lazy(async () => ({ default: (await import("./pages/SessionSetup")).SessionSetup }));
const HostStudio = React.lazy(async () => ({ default: (await import("./pages/HostStudio")).HostStudio }));
const GuestJoin = React.lazy(async () => ({ default: (await import("./pages/GuestJoin")).GuestJoin }));
const RecordingDetail = React.lazy(async () => ({ default: (await import("./pages/RecordingDetail")).RecordingDetail }));
const RecordingFiles = React.lazy(async () => ({ default: (await import("./pages/RecordingFiles")).RecordingFiles }));
const Landing = React.lazy(async () => ({ default: (await import("./pages/Landing")).Landing }));
const RoomJoin = React.lazy(async () => ({ default: (await import("./pages/RoomJoin")).RoomJoin }));
const RoomRecordings = React.lazy(async () => ({ default: (await import("./pages/RoomRecordings")).RoomRecordings }));
const RoomSettings = React.lazy(async () => ({ default: (await import("./pages/RoomSettings")).RoomSettings }));
const RecoveryCenter = React.lazy(async () => ({ default: (await import("./pages/RecoveryCenter")).RecoveryCenter }));
const AccountSettings = React.lazy(async () => ({ default: (await import("./pages/AccountSettings")).AccountSettings }));
const SchedulePage = React.lazy(async () => ({ default: (await import("./pages/SchedulePage")).SchedulePage }));

function RouteLoading() {
  return (
    <main className="page" aria-busy="true">
      <div className="spinner-wrap" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>Loading…</span>
      </div>
    </main>
  );
}

function RouteMeta({ title }: { title: string }) {
  React.useEffect(() => {
    document.title = title === "record a podcast" ? title : `${title} · record a podcast`;
    const frame = window.requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>("main h1");
      if (!heading) return;
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [title]);
  return <span className="visually-hidden" aria-live="polite" aria-atomic="true">{title}</span>;
}

function route(element: React.ReactNode, title: string) {
  return (
    <React.Suspense fallback={<RouteLoading />}>
      <RouteMeta title={title} />
      {element}
    </React.Suspense>
  );
}

function RouteError() {
  const error = useRouteError();
  const chunkFailed = error instanceof Error && /dynamically imported module|failed to fetch/i.test(error.message);
  return (
    <main className="page page-narrow route-error-page">
      <h1>{chunkFailed ? "The app was updated" : "This page could not open"}</h1>
      <p className="hint">{chunkFailed ? "Reload to use the latest version." : "The address may be stale, or the page hit a temporary problem."}</p>
      <div className="button-row">
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Reload page</button>
        <Link className="btn" to="/app">Go to recordings</Link>
      </div>
    </main>
  );
}

function NotFound() {
  return (
    <main className="page page-narrow route-error-page">
      <h1>Page not found</h1>
      <p className="hint">Check the address or return to your recordings.</p>
      <Link className="btn btn-primary" to="/app">Go to recordings</Link>
    </main>
  );
}

const routeError = <RouteError />;
const router = createBrowserRouter([
  { path: "/", element: route(<Landing />, "record a podcast"), errorElement: routeError },
  { path: "/sign-in/*", element: route(<SignInPage />, "Sign in"), errorElement: routeError },
  { path: "/sign-up/*", element: route(<SignUpPage />, "Create account"), errorElement: routeError },
  { path: "/app", element: <ProtectedRoute>{route(<Dashboard />, "Recordings")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/schedule", element: <ProtectedRoute>{route(<SchedulePage />, "Schedule")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/rooms/:roomId", element: <ProtectedRoute>{route(<RoomRecordings />, "Room recordings")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/rooms/:roomId/settings", element: <ProtectedRoute>{route(<RoomSettings />, "Room settings")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/settings", element: <ProtectedRoute>{route(<AccountSettings />, "Settings")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/recovery", element: <ProtectedRoute>{route(<RecoveryCenter />, "Recovery")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/workspace", element: <Navigate to="/app/settings" replace /> },
  // Recovery is authorized by the participant token stored in this browser,
  // so guests must be able to reach it without a Clerk account.
  { path: "/recovery", element: route(<RecoveryCenter />, "Recovery"), errorElement: routeError },
  { path: "/app/sessions/:sessionId", element: <ProtectedRoute>{route(<SessionSetup />, "Recording setup")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/sessions/:sessionId/studio", element: <ProtectedRoute>{route(<HostStudio />, "Studio")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/sessions/:sessionId/detail", element: <ProtectedRoute>{route(<RecordingDetail />, "Recording")}</ProtectedRoute>, errorElement: routeError },
  { path: "/app/sessions/:sessionId/detail/files", element: <ProtectedRoute>{route(<RecordingFiles />, "Recording files")}</ProtectedRoute>, errorElement: routeError },
  { path: "/invite/:sessionId", element: route(<GuestJoin />, "Join recording"), errorElement: routeError },
  // Keep existing links working while new invitations use the cleaner /invite path.
  { path: "/join/:sessionId", element: route(<GuestJoin />, "Join recording"), errorElement: routeError },
  { path: "/:username/rooms/:roomSlug", element: route(<RoomJoin />, "Join room"), errorElement: routeError },
  { path: "*", element: route(<NotFound />, "Page not found"), errorElement: routeError },
]);

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppAuthProvider><RouterProvider router={router} /></AppAuthProvider>
      <Toaster
        theme="dark"
        position="bottom-right"
        mobileOffset={{ right: 16, bottom: 172, left: 16 }}
        closeButton
        toastOptions={{
          classNames: {
            toast: "app-toast",
            title: "app-toast-title",
            description: "app-toast-description",
            actionButton: "app-toast-action",
            closeButton: "app-toast-close",
          },
        }}
      />
    </QueryClientProvider>
  </React.StrictMode>,
);
